import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AgmMap, LatLng, LatLngBounds } from '@agm/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Coordinates } from '../../../../classes/Coordinates';
import { MapControlDirective } from '../../../../directives/map-control.directive';
import { WeatherStationsService } from '../../../../models/weather-station-stats/service';
import { IWeatherStationPublicListItem, IWeatherStationViewModel } from '../../../weather-stations/interfaces';
import { SharedKeepDiscardDialog } from '../../../shared/dialogs/keep-discard-dialog';
import { SharedUpdateService } from '../../../shared/dialogs/update.service';
import { IMarker, IMarkerCollection, IWeatherStationCurrentModel } from '../../interfaces';
import { IMapIcon, IMapIconSize } from '../../modals/weather-station/interfaces';

@Component({
	moduleId: module.id,
	selector: 'ranch-weather-station-form',
	templateUrl: 'main.html',
	styleUrls: ['main.scss']
})

export class RanchWeatherStationFormComponent implements OnInit, OnDestroy {
	@Input()
	public ranchCoordinates: string;

	@Input()
	public selectedStationIds: number[] = [];

	@Input()
	public isRanchAgnostic = false;

	@Output()
	public onCancel: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onHide: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onShow: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onSubmit: EventEmitter<IMarker[]> = new EventEmitter<IMarker[]>();

	public loadingData = true;
	public ranchLocation: IMarker = null;
	public showWeatherStationsList = false;
	public weatherStations: IMarker[] = new Array();
	public zoomOutWarning = false;

	@ViewChild(AgmMap, { static: false }) private _map: AgmMap;
	@ViewChild(MapControlDirective, { static: false }) private _mapControl: MapControlDirective;

	private _icons: {
		selected: IMapIcon,
		unselected: IMapIcon
	};
	private readonly _iconSizes: {
		regular: IMapIconSize,
		small: IMapIconSize
	}
	private readonly _mininumZoomLevel: number = 7;
	private _originalStations: IMarker[] = new Array();
	private _subscriptions$: Subject<boolean>;
	private _userMadeChanges: boolean;
	private _zoom = 10;

	constructor(
		private _dialog: MatDialog,
		private _sharedUpdateService: SharedUpdateService,
		private _weatherStationService: WeatherStationsService
	) {
		this._iconSizes = {
			regular: {
				height: 50,
				width: 80
			},
			small: {
				height: 10,
				width: 10
			}
		};

		this._icons = {
			selected: {
				url: '../assets/images/icons/map_marker-weather_station-selected.png',
				scaledSize: this._iconSizes.regular
			},
			unselected: {
				url: '../assets/images/icons/map_marker-weather_station.png',
				scaledSize: this._iconSizes.regular
			}
		};
	}

	public ngOnInit(): void {
		this._subscriptions$ = new Subject();
		this._clear();
		this._getWeatherStations();
		this._setupPopupSubscriptions();
	}

	ngOnDestroy(): void {
		if (!this._subscriptions$) {
			return;
		}

		this._subscriptions$.next(true);
		this._subscriptions$.complete();
	}

	public cancel(): void {
		if (this._userMadeChanges) {
			this._dialog.open(SharedKeepDiscardDialog, {
				width: '500px',
				disableClose: true,
			});

			this.onHide.emit();
		} else {
			this.onCancel.emit();
		}
	}

	public getStationId(stationId: string): string {
		if (this._zoom < this._mininumZoomLevel) {
			return null;
		}

		return stationId;
	}

	public getStationMarker(station: IMarker): IMapIcon {
		if (this._zoom < this._mininumZoomLevel) {
			this._icons.selected.url = '../assets/images/icons/map_marker-weather_station-selected-small.png';
			this._icons.unselected.url = '../assets/images/icons/map_marker-weather_station-small.png';
		} else {
			this._icons.selected.url = '../assets/images/icons/map_marker-weather_station-selected.png';
			this._icons.unselected.url = '../assets/images/icons/map_marker-weather_station.png';
		}

		if (station.Selected || station.isCurrent) {
			return this._icons.selected;
		} else {
			return this._icons.unselected;
		}
	}

	public resetMapOnboundsChange(bounds: LatLngBounds): void {
		this._mapControl.getZoom().then(res => {
			let zoom = Number(res);

			if (zoom !== this._zoom && this.showWeatherStationsList) {
				this._zoom = zoom;
				this._scaleMarkerIconSizes(this._zoom);
			}

			this._setWeatherStations(bounds);
		});
	}

	public selectStation(selectedStation: IMarker): void {
		if (!selectedStation) {
			return;
		}

		if (selectedStation.isCurrent) {
			return;
		}

		for (let station of this.weatherStations) {
			if (station.Id === selectedStation.Id) {
				station.Selected = !station.Selected;
				this._userMadeChanges = true;
				break;
			}
		}
		for (let station of this._originalStations) {
			if (station.Id === selectedStation.Id) {
				station.Selected = !station.Selected;
				break;
			}
		}
	}

	public submitSelectedStations(): void {
		this.onSubmit.emit(this._originalStations.filter(data => data.Selected));
	}

	private _buildMarkerFromPublicStation(station: IWeatherStationPublicListItem, ranchCoordinates: Coordinates): IMarker {
		return {
			Id: station.Id,
			External_Id: null,
			Longitude: station.Lng,
			Latitude: station.Lat,
			Selected: false,
			Name: station.Name,
			Distance: ranchCoordinates ? this._getDistanceInMiles(ranchCoordinates.lat, ranchCoordinates.lng, station.Lat, station.Lng) : null,
			NearestCity: null,
			County: null,
			isCurrent: false
		};
	}

	private _buildMarkerFromStation(station: IWeatherStationViewModel): IMarker {
		return {
			Id: station.Id,
			External_Id: station.External_Id,
			Longitude: station.Lng,
			Latitude: station.Lat,
			Selected: false,
			Name: station.Name,
			Distance: station.Distance,
			NearestCity: station.NearestCity,
			County: station.County,
			isCurrent: false
		};
	}

	private _clear(): void {
		this.weatherStations = new Array();
		this.ranchLocation = null;
		this._originalStations = new Array();
		this._userMadeChanges = false;
		this._zoom = 10;
		this.showWeatherStationsList = false;
		this._icons.selected.scaledSize = this._iconSizes.regular;
		this._icons.unselected.scaledSize = this._iconSizes.regular;
		this.zoomOutWarning = false;
		this.loadingData = true;
	}

	private _getDistanceInMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
		let earthRadiusMiles = 3958.8;
		let dLat = this._toRadians(lat2 - lat1);
		let dLng = this._toRadians(lng2 - lng1);
		let a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(this._toRadians(lat1)) * Math.cos(this._toRadians(lat2)) *
			Math.sin(dLng / 2) * Math.sin(dLng / 2);
		let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

		return Math.round(earthRadiusMiles * c * 10) / 10;
	}

	private _getWeatherStations(): void {
		(this.isRanchAgnostic ? this._fetchWeatherStations() : this._fetchRanchWeatherStations()).then(() => {
			this.weatherStations = this._sortStationsByDistanceFromRanch(this.weatherStations);
			this._originalStations = this._sortStationsByDistanceFromRanch(this._originalStations);
			this.loadingData = false;
			this._resetMap();
		});
	}

	private _fetchRanchWeatherStations(): Promise<void> {
		let promises: {
			ranchStations: Promise<IMarkerCollection>,
			listAll: Promise<IWeatherStationViewModel[]>,
			currentModels: Promise<IWeatherStationCurrentModel[]>
		};

		promises = {
			ranchStations: this._weatherStationService.listRanchStations(),
			listAll: this._weatherStationService.listAll(),
			currentModels: this._weatherStationService.getRanchWeatherStations()
		};

		return Promise.all([promises.ranchStations, promises.listAll, promises.currentModels]).
			then((res: [IMarkerCollection, IWeatherStationViewModel[], IWeatherStationCurrentModel[]]) => {
			let allStations: IMarkerCollection;
			let associatedStations: IWeatherStationCurrentModel[];

			allStations = this._weatherStationService.combineStationArrays(res[0], res[1]);
			associatedStations = res[2];

			this.ranchLocation = allStations.RanchLocation;

			for (let station of allStations.Stations) {
				station.isCurrent = false;

				for (let currentStation of associatedStations) {
					if (station.Id === currentStation.Id) {
						station.isCurrent = true;
					}
				}

				if (!station.isCurrent) {
					this.weatherStations.push(station);
					this._originalStations.push(station);
				}
			}
		});
	}

	private _fetchWeatherStations(): Promise<void> {
		let ranchCoordinates: Coordinates = this.ranchCoordinates ? new Coordinates(this.ranchCoordinates) : null;

		if (ranchCoordinates) {
			this.ranchLocation = {
				Id: null,
				External_Id: null,
				Longitude: ranchCoordinates.lng,
				Latitude: ranchCoordinates.lat,
				Selected: false,
				Name: null,
				Distance: null,
				NearestCity: null,
				County: null
			};
		}

		return this._weatherStationService.listActivePublic().then((stations: IWeatherStationPublicListItem[]) => {
			if (!stations || !Array.isArray(stations)) {
				return;
			}

			for (let station of stations) {
				if (this.selectedStationIds.indexOf(station.Id) !== -1) {
					continue;
				}

				let marker = this._buildMarkerFromPublicStation(station, ranchCoordinates);
				this.weatherStations.push(marker);
				this._originalStations.push(marker);
			}
		});
	}

	private _recenterMap(lat: number, lng: number, zoom: number): Promise<void> {
		let promises: Promise<void>[] = new Array();
		promises.push(this._mapControl.centerMap(lat, lng));
		promises.push(this._mapControl.setZoom(zoom));

		return Promise.all(promises).then(() => {
			this._setInitialBounds().then(() => {
				this.showWeatherStationsList = true;
			});
		});
	}

	private _resetMap(): void {
		if (!this._map || !this.ranchLocation) {
			this.showWeatherStationsList = true;
			return;
		}

		this._map.triggerResize().then(() => {
			this._recenterMap(this.ranchLocation.Latitude, this.ranchLocation.Longitude, this._zoom);
		});
	}

	private _scaleMarkerIconSizes(zoom: number): void {
		let scaledSize: {
			height: number,
			width: number
		} = this._iconSizes.regular;

		if (zoom < 10 && zoom >= this._mininumZoomLevel) {
			scaledSize = {
				height: this._iconSizes.regular.height * ((zoom - 5) / 5),
				width: this._iconSizes.regular.width * ((zoom - 5) / 5)
			};
		} else if (zoom < this._mininumZoomLevel) {
			scaledSize = this._iconSizes.small;
		}

		this._icons.selected.scaledSize = scaledSize;
		this._icons.unselected.scaledSize = scaledSize;
	}

	private _setInitialBounds(): Promise<void> {
		return this._mapControl.getBounds()
			.then(bounds => {
				this.resetMapOnboundsChange(bounds);
			});
	}

	private _setWeatherStations(bounds: LatLngBounds): void {
		let filteredStations: IMarker[];
		let stationsWithinBounds: IMarker[];
		let ne: LatLng;
		let sw: LatLng;

		this.zoomOutWarning = false;

		filteredStations = new Array();
		stationsWithinBounds = new Array();

		if (!bounds) {
			return;
		}

		ne = bounds.getNorthEast();
		sw = bounds.getSouthWest();

		if (!ne || !sw) {
			return;
		}

		for (let station of this._originalStations) {
			if (station.Latitude >= sw.lat() && station.Latitude <= ne.lat() &&
				station.Longitude >= sw.lng() && station.Longitude <= ne.lng()) {
				stationsWithinBounds.push(station);
			}
		}

		if (!stationsWithinBounds || stationsWithinBounds.length === 0) {
			this.zoomOutWarning = true;

			filteredStations = this._originalStations
				.filter(data => data.Selected);
		} else {
			filteredStations = this._originalStations
				.filter(data => data.Selected ||
					(data.Latitude >= sw.lat() && data.Latitude <= ne.lat() &&
						data.Longitude >= sw.lng() && data.Longitude <= ne.lng()));
		}

		this.weatherStations = new Array();

		for (let station of filteredStations) {
			this.weatherStations.push(Object.assign({}, station));
		}
	}

	private _setupPopupSubscriptions(): void {
		this._sharedUpdateService.keep$
			.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.onShow.emit();
		});

		this._sharedUpdateService.discard$
			.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.onCancel.emit();
		});
	}

	private _sortStationsByDistanceFromRanch(stations: IMarker[]): IMarker[] {
		if (!stations) {
			return new Array();
		}

		stations.sort((a, b) => {
			if (a.Distance === null || a.Distance === undefined) {
				return 1;
			}

			if (b.Distance === null || b.Distance === undefined) {
				return -1;
			}

			return a.Distance - b.Distance;
		});

		return stations;
	}

	private _toRadians(degrees: number): number {
		return degrees * Math.PI / 180;
	}
}
